import React, {useState, useEffect} from "react";
import {
  Box,
  Grid,
  TextField,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import {Form, Formik} from "formik";
import * as Yup from "yup";
import api from "API/api";
import {toast} from "sonner";

const ScheduleForm = ({user, setUserData}) => {
  const [timeInputType, setTimeInputType] = useState("text");
  const [scheduleData, setScheduleData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.preferences && Object.keys(user.preferences).length > 0) {
      setScheduleData(user.preferences);
    } else {
      setScheduleData(null);
    }
  }, [user.preferences]);

  const isEdit = user?.preferences && Object.keys(user?.preferences).length > 0;

  const initialValues = scheduleData || {
    frequency: "",
    scheduled_day: "",
    scheduled_time: "",
    interval_hours: "",
  };

  const validationSchema = Yup.object().shape({
    frequency: Yup.string().required("Frequency is required"),
    scheduled_day: Yup.string().when("frequency", (frequency, schema) =>
      frequency === "weekly"
        ? schema.required("Scheduled day is required")
        : schema
    ),
    scheduled_time: Yup.string().required("Scheduled time is required"),
    interval_hours: Yup.number()
      .required("Interval hours is required")
      .positive("Must be a positive number"),
  });

  const handleSubmit = async (values, actions) => {
    const formattedValues = {
      ...values,
      scheduled_time: values.scheduled_time || "",
    };

    try {
      setLoading(true);
      let response;
      if (scheduleData) {
        response = await api.post(`/analytics/preferences`, formattedValues);
      } else {
        response = await api.post(`/analytics/preferences`, formattedValues);
      }
      toast.success(response?.data?.message);
      if (response?.data?.data) {
        const updatedUser = {
          ...user,
          preferences: formattedValues,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUserData(updatedUser);
        setScheduleData(formattedValues);
        actions.resetForm();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message)
    } finally {
      actions.setSubmitting(false);
      setLoading(false);
    }
  };

  return (
    <Box>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({values, errors, touched, handleChange, handleBlur, isSubmitting}) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Select Frequency</InputLabel>
                  <Select
                    name="frequency"
                    value={values.frequency}
                    label="Select Frequency"
                    onChange={handleChange}
                    sx={{height: "45px"}}
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                  </Select>
                </FormControl>
                {errors.frequency && touched.frequency && (
                  <div style={{color: "red", fontSize: "12px"}}>
                    {errors.frequency}
                  </div>
                )}
              </Grid>

              {values.frequency === "weekly" && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Select Schedule Day</InputLabel>
                    <Select
                      name="scheduled_day"
                      value={values.scheduled_day}
                      label="Select Schedule Day"
                      onChange={handleChange}
                      sx={{height: "45px"}}
                    >
                      <MenuItem value="monday">Monday</MenuItem>
                      <MenuItem value="tuesday">Tuesday</MenuItem>
                      <MenuItem value="wednesday">Wednesday</MenuItem>
                      <MenuItem value="thursday">Thursday</MenuItem>
                      <MenuItem value="friday">Friday</MenuItem>
                      <MenuItem value="saturday">Saturday</MenuItem>
                      <MenuItem value="sunday">Sunday</MenuItem>
                    </Select>
                  </FormControl>
                  {errors.scheduled_day && touched.scheduled_day && (
                    <div style={{color: "red", fontSize: "12px"}}>
                      {errors.scheduled_day}
                    </div>
                  )}
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="scheduled_time"
                  label="Scheduled Time"
                  placeholder="Enter Scheduled Time"
                  type={timeInputType}
                  value={values.scheduled_time}
                  onFocus={() => setTimeInputType("time")}
                  onBlur={(e) => {
                    handleBlur(e);
                    if (!values.scheduled_time) {
                      setTimeInputType("text");
                    }
                  }}
                  onChange={handleChange}
                  error={
                    touched.scheduled_time && Boolean(errors.scheduled_time)
                  }
                  helperText={touched.scheduled_time && errors.scheduled_time}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="interval_hours"
                  label="Interval Hours"
                  type="number"
                  value={values.interval_hours}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.interval_hours && Boolean(errors.interval_hours)
                  }
                  helperText={touched.interval_hours && errors.interval_hours}
                />
              </Grid>

              <Grid item xs={12} sx={{display: "flex", justifyContent: "end"}}>
                <Button
                  variant="contained"
                  type="submit"
                  sx={{
                    background: "#063863",
                    color: "white !important",
                    fontSize: "14px",
                    fontWeight: "500",
                    width: "245px",
                    height: "45px",
                    "&:hover": {background: "#063863", color: "white"},
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress
                      size={25}
                      sx={{
                        color: "white !important",
                      }}
                    />
                  ) : isSubmitting ? (
                    "Update Preferences"
                  ) : (
                    "Save Preferences"
                  )}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ScheduleForm;
